import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex, Input, Skeleton, Text } from "@chakra-ui/react";
import SkuDashboardHeader from "@/features/sku-dashboard/components/SkuDashboardHeader";
import SkuResultSection from "@/features/sku-dashboard/components/SkuResultSection";
import BestSkuSection from "@/features/sku-dashboard/components/BestSkuSection";
import useGetSku, { SkuInfo } from "@/features/sku-dashboard/hooks/use-get-sku";
import AppButton from "@/features/line-sheet-sets/app-button";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import SVG from "react-inlinesvg";
import { Navigate, useSearchParams } from "react-router-dom";
import useIdentity from "@/features/ui/hooks/use-identity";
import useGetMySelf from "@/features/auth/use-get-my-self";

function ResultSkeleton() {
  // 검색 결과 자리의 뼈대 — 실제 결과(왼쪽 이미지·라인시트 목록, 오른쪽 그래프)와 같은 배치
  return (
    <Flex gap={"16px"}>
      <Flex flexDirection={"column"} gap={"12px"} flex={1} minW={0}>
        <Flex gap={"8px"}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} width={"120px"} height={"120px"} borderRadius={"8px"} />
          ))}
        </Flex>
        <Skeleton height={"200px"} borderRadius={"8px"} />
      </Flex>
      <Skeleton flex={1} minW={0} height={"332px"} borderRadius={"8px"} />
    </Flex>
  );
}

export default function SkuDashboardPage() {
  const { t, tTitle, tSentence } = useI18nHelper();
  const { error: showError } = useAppToasts();
  const [searchParams, setSearchParams] = useSearchParams();
  const identity = useIdentity();
  const { data: mySelf, isLoading: isMySelfLoading } = useGetMySelf();

  // 모든 바이어가 볼 수 있다(2026-11-04, 전엔 SKU_DASHBOARD 기능 플래그가 있는 바이어만)
  const canAccess = useMemo(() => {
    if (!mySelf || !identity?.company) return null;
    return identity.company.type === "BUYER";
  }, [mySelf, identity?.company]);
  const [inputValue, setInputValue] = useState(searchParams.get("sku") ?? "");
  const [searchedSku, setSearchedSku] = useState("");
  const [result, setResult] = useState<SkuInfo | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const showErrorRef = useRef(showError);
  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  const { fire: fireGetSku, isLoading } = useGetSku();

  const handleSearch = useCallback(async (skuOverride?: string) => {
    const sku = (skuOverride ?? inputValue).trim();
    if (!sku) return;
    setResult(null);
    setSearchedSku(sku);
    setSearchParams({ sku }, { replace: true });
    try {
      const data = await fireGetSku({ sku, buyerId: identity?.company?.id }, {});
      setResult(data);
    } catch (e: any) {
      showErrorRef.current(e);
    }
  }, [inputValue, fireGetSku, setSearchParams, identity?.company?.id]);

  const handleRefresh = useCallback(async () => {
    if (!searchedSku) return;
    try {
      const data = await fireGetSku({ sku: searchedSku, buyerId: identity?.company?.id }, {});
      setResult(data);
    } catch (e: any) {
      showErrorRef.current(e);
    }
  }, [searchedSku, fireGetSku, identity?.company?.id]);

  const handleClear = useCallback(() => {
    setInputValue("");
    setSearchedSku("");
    setResult(null);
    setSearchParams({}, { replace: true });
    inputRef.current?.focus();
  }, [setSearchParams]);

  // URL에 sku 파라미터가 있으면 자동 검색
  useEffect(() => {
    const skuParam = searchParams.get("sku");
    if (skuParam) {
      handleSearch(skuParam);
    }
    // 최초 마운트 시에만 실행
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") handleSearch();
      if (e.key === "Escape") handleClear();
    },
    [handleSearch, handleClear]
  );

  if (canAccess === false) {
    return <Navigate to={"/"} replace />;
  }
  if (canAccess === null || isMySelfLoading) {
    return null;
  }

  return (
    <Flex
      flexDirection={"column"}
      height={"100%"}
      maxH={"calc(100vh - 52px)"}
      minW={"1136px"}
    >
      <SkuDashboardHeader />

      <Flex
        flexDirection={"column"}
        padding={"8px"}
        gap={"16px"}
        overflowY={"auto"}
        flex={1}
      >
        {/* 인기 SKU 영역 — 카드를 누르면 아래에서 바로 검색된다 */}
        <BestSkuSection
          selectedSku={searchedSku}
          onSelect={(sku) => {
            setInputValue(sku);
            handleSearch(sku);
          }}
        />

        {/* 검색 영역 */}
        <Flex
          borderRadius={"8px"}
          boxShadow={"0 0 0 0.5px #D4D4D1"}
          background={"#FFFFFF"}
          padding={"8px 16px"}
          gap={"8px"}
          alignItems={"center"}
          _focusWithin={{ boxShadow: "0 0 0 1.5px #1272EF" }}
          transition={"box-shadow 0.15s"}
        >
          <SVG
            src={"/icons-v1_1/search.svg"}
            width={16}
            height={16}
            color={"#8F8F8C"}
          />
          <Input
            ref={inputRef}
            flex={1}
            placeholder={t("sku_search_placeholder")}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            border={"none"}
            _focus={{ boxShadow: "none" }}
            textStyle={"body-md-regular"}
            color={"#1A1A1A"}
            _placeholder={{ color: "#8F8F8C" }}
            size={"sm"}
            height={"28px"}
            padding={"0"}
            autoComplete={"off"}
            spellCheck={false}
          />
          {inputValue && (
            <Box
              as={"button"}
              type={"button"}
              aria-label={t("clear")}
              title={t("clear")}
              onClick={handleClear}
              color={"#8F8F8C"}
              display={"flex"}
              alignItems={"center"}
              padding={"2px"}
              borderRadius={"4px"}
              _hover={{ color: "#444444", backgroundColor: "#F2F2EF" }}
            >
              <SVG src={"/icons-v1_1/x-circle-solid.svg"} width={16} height={16} />
            </Box>
          )}
          <AppButton
            isLoading={isLoading}
            isDisabled={!inputValue.trim()}
            onClick={() => handleSearch()}
            height={"28px"}
          >
            {tTitle("search")}
          </AppButton>
        </Flex>

        {/* 결과 영역 */}
        {isLoading && (
          <Flex flexDirection={"column"} gap={"8px"}>
            <Text textStyle={"caption-regular"} color={"#8F8F8C"}>
              {t("sku_searching", { sku: searchedSku })}
            </Text>
            <ResultSkeleton />
          </Flex>
        )}

        {!isLoading && result && (
          <SkuResultSection data={result} onRefresh={handleRefresh} />
        )}

        {!isLoading && !result && searchedSku && (
          <Flex
            alignItems={"center"}
            justifyContent={"center"}
            height={"320px"}
            flexDirection={"column"}
            gap={"8px"}
          >
            <SVG
              src={"/icons-v1_1/search.svg"}
              width={32}
              height={32}
              color={"#D4D4D1"}
            />
            <Text textStyle={"body-md-regular"} color={"#8F8F8C"}>
              {tSentence("sku_not_found")}
            </Text>
            <Text textStyle={"caption-regular"} color={"#B5B5B2"}>
              {searchedSku}
            </Text>
          </Flex>
        )}

        {!isLoading && !result && !searchedSku && (
          <Flex
            alignItems={"center"}
            justifyContent={"center"}
            height={"320px"}
            flexDirection={"column"}
            gap={"8px"}
          >
            <Box opacity={0.3}>
              <SVG
                src={"/icons-v1_1/search.svg"}
                width={40}
                height={40}
                color={"#8F8F8C"}
              />
            </Box>
            <Text textStyle={"body-md-regular"} color={"#8F8F8C"}>
              {t("sku_search_guide")}
            </Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}
